/**
 * Tre schermate: `/wizard` (percorso di benvenuto), `/oggi` (la casa dell'app, dopo il benvenuto)
 * e `/kit` (vetrina del design system). Nessun router: basta il pathname.
 * Prima di tutto la sessione (`GET /auth/session`): senza, la schermata di accesso.
 * Dalla radice si va al benvenuto finche non e completato, poi a Oggi.
 */
import { useEffect, useState } from 'react'

import { api, ApiFailure } from './api/client'
import { wizardApi } from './api/wizard'
import { LoginScreen } from './auth/LoginScreen'
import { DocumentiRoute } from './documenti/DocumentiRoute'
import { Intro } from './intro/Intro'
import { KitPage } from './kit/KitPage'
import { OggiRoute } from './oggi/OggiRoute'
import { registraServiceWorker } from './push'
import { WizardRoute } from './wizard/WizardRoute'

const WIZARD_PATH = '/wizard'
const OGGI_PATH = '/oggi'
/** Ricette, fogli e foto (DEC-23). */
const DOCUMENTI_PATH = '/documenti'
/** Vetrina del design system (lane E): non collegata alla navigazione dell'app. */
const KIT_PATH = '/kit'

type Auth = 'verifica' | 'entrata' | 'da_entrare'

async function destinazione(): Promise<string> {
  try {
    const vista = await wizardApi.session()
    return vista.session.status === 'completato' ? OGGI_PATH : WIZARD_PATH
  } catch {
    return WIZARD_PATH
  }
}

/** L'intro parte a ogni avvio dell'app: sessionStorage si svuota quando l'app viene chiusa. */
const INTRO_KEY = 'mia-intro-vista'

function introGiaVista(): boolean {
  try {
    return sessionStorage.getItem(INTRO_KEY) === '1'
  } catch {
    return false
  }
}

export function App() {
  const [path, setPath] = useState(window.location.pathname)
  const [auth, setAuth] = useState<Auth>('verifica')
  const [intro, setIntro] = useState(() => !introGiaVista())
  /** undefined = nome non ancora letto: l'intro aspetta, cosi il saluto nasce gia col nome. */
  const [nome, setNome] = useState<string | null | undefined>(undefined)

  const chiudiIntro = () => {
    try {
      sessionStorage.setItem(INTRO_KEY, '1')
    } catch {
      // senza storage l'intro si chiude lo stesso
    }
    setIntro(false)
  }

  useEffect(() => {
    if (auth !== 'entrata' || !intro) return
    void wizardApi
      .session()
      .then((vista) => setNome(vista.display_form))
      .catch(() => setNome(null))
  }, [auth, intro])

  useEffect(() => {
    if (path === KIT_PATH) return
    void registraServiceWorker()
    let vivo = true
    api
      .get('/auth/session')
      .then(() => vivo && setAuth('entrata'))
      .catch((failure) => {
        // 401 = nessuna sessione: si entra. Rete assente o altro: la schermata mostra il suo rimedio.
        if (vivo) setAuth(failure instanceof ApiFailure && failure.status === 401 ? 'da_entrare' : 'entrata')
      })
    return () => {
      vivo = false
    }
  }, [path])

  useEffect(() => {
    if (auth !== 'entrata' || [WIZARD_PATH, OGGI_PATH, DOCUMENTI_PATH, KIT_PATH].includes(path)) return
    let vivo = true
    void destinazione().then((dove) => {
      if (!vivo) return
      window.history.replaceState(null, '', dove)
      setPath(dove)
    })
    return () => {
      vivo = false
    }
  }, [auth, path])

  if (path === KIT_PATH) return <KitPage />

  if (auth === 'verifica') return <main />
  if (auth === 'da_entrare') {
    return (
      <main>
        <LoginScreen onLoggedIn={() => setAuth('entrata')} />
      </main>
    )
  }

  const pagina =
    path === OGGI_PATH ? (
      <OggiRoute />
    ) : path === WIZARD_PATH ? (
      <WizardRoute />
    ) : path === DOCUMENTI_PATH ? (
      <DocumentiRoute />
    ) : null
  return (
    <main>
      {pagina}
      {intro && (nome === undefined ? <div className="intro" /> : <Intro nome={nome} onDone={chiudiIntro} />)}
    </main>
  )
}
